import { useMemo } from 'react'
import { Combobox, type ComboboxOption, type ComboboxProps } from './combobox'

/** Used only where the browser cannot list its zones. */
const FALLBACK_TIME_ZONES = ['Africa/Cairo', 'Asia/Riyadh', 'Asia/Dubai', 'Europe/London', 'UTC']

/**
 * One formatter per zone for the life of the page. Building an
 * Intl.DateTimeFormat is the expensive step, and the list builds about 420 of
 * them on every mount; the formatter has no date in it, so the offset below
 * is still computed for the date asked.
 */
const offsetFormatters = new Map<string, Intl.DateTimeFormat>()

function offsetFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = offsetFormatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
    offsetFormatters.set(timeZone, formatter)
  }
  return formatter
}

/** The offset the zone has today, e.g. `GMT+02:00`. It changes with daylight saving time. */
export function timeZoneOffsetLabel(timeZone: string, at: Date = new Date()): string {
  const part = offsetFormatter(timeZone)
    .formatToParts(at)
    .find((item) => item.type === 'timeZoneName')
  return part?.value ?? timeZone
}

/** Every IANA zone the browser knows, plus UTC and the saved value, sorted. */
export function timeZoneOptions(current?: string | null): ComboboxOption[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : FALLBACK_TIME_ZONES
  const zones = new Set([...supported, 'UTC'])
  if (current) zones.add(current)
  return [...zones].sort().map((zone) => {
    const offset = timeZoneOffsetLabel(zone)
    return {
      value: zone,
      label: zone,
      displayLabel: `${zone} (${offset})`,
      description: offset,
      keywords: [zone.replaceAll('_', ' '), offset],
    }
  })
}

export type TimeZoneComboboxProps = Omit<ComboboxProps, 'options' | 'filter'>

/**
 * A searchable list of IANA time zones. It stores a zone name, never a fixed
 * offset, so the server applies daylight saving time by itself.
 */
export function TimeZoneCombobox({ value, ...props }: TimeZoneComboboxProps) {
  const options = useMemo(() => timeZoneOptions(value), [value])
  return <Combobox value={value} options={options} {...props} />
}
